import { useCallback, useEffect, useState } from 'react'
import type { OliviumAlertApiRow } from '../api/client'
import { api, ApiError, isAuthError } from '../api/client'
import { getRuntime } from './_mode'
import { captureHookFailure, recordHookOutcome } from '../observability/sentry'

export interface OliviumAlertsState {
  alerts: OliviumAlertApiRow[]
  loading: boolean
  error: string | null
  authRequired: boolean
  refresh: () => Promise<void>
  updateAlert: (
    id: string,
    status: 'acknowledged' | 'investigating' | 'resolved' | 'dismissed' | 'escalated',
  ) => Promise<OliviumAlertApiRow | null>
}

export function useOliviumAlerts(): OliviumAlertsState {
  const [alerts, setAlerts] = useState<OliviumAlertApiRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAuthRequired(false)

    const { live } = getRuntime()
    if (!live) {
      setAlerts([])
      setLoading(false)
      recordHookOutcome('useOliviumAlerts', 'demo_fallback', {
        route: '/api/olivium/alerts',
        rows: 0,
      })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.olivium.alerts({ limit: 100 })
      setAlerts(result.alerts || [])
      recordHookOutcome('useOliviumAlerts', 'live_success', {
        route: '/api/olivium/alerts',
        rows: result.alerts?.length || 0,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      if (isAuthError(err)) {
        setAuthRequired(true)
        setAlerts([])
      } else {
        const apiErr = err as ApiError
        setAlerts([])
        setError(apiErr?.message || 'olivium_alerts_failed')
        captureHookFailure('useOliviumAlerts', 'live_error', err, {
          route: '/api/olivium/alerts',
          code: apiErr?.code,
          status: apiErr?.status,
          durationMs: Date.now() - startedAt,
        })
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const updateAlert = useCallback<
    OliviumAlertsState['updateAlert']
  >(async (id, status) => {
    try {
      const result = await api.olivium.updateAlert({ id, status })
      setAlerts((prev) =>
        prev.map((alert) => (alert.id === id ? result.alert : alert)),
      )
      return result.alert
    } catch (err) {
      const apiErr = err as ApiError
      setError(apiErr?.message || 'olivium_alert_update_failed')
      return null
    }
  }, [])

  return {
    alerts,
    loading,
    error,
    authRequired,
    refresh: load,
    updateAlert,
  }
}
